// src/pages/Profile.js

import React, { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Box
} from '@mui/material'
import { useAuth } from '../contexts/AuthContext'
import { db } from '../firebase'
import { doc, getDoc } from 'firebase/firestore'
import { useNavigate } from 'react-router-dom'
import AvatarSection from '../components/Profile/AvatarSection'
import ProfileForm from '../components/Profile/ProfileForm'
import PasswordUpdateForm from '../components/Profile/PasswordUpdateForm'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function Profile() {
  const { currentUser } = useAuth()
  const navigate = useNavigate()

  const { t } = useTranslation() // <--- i18n Hook

  const [userData, setUserData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) return
      try {
        const userDoc = doc(db, 'users', currentUser.uid)
        const docSnap = await getDoc(userDoc)
        if (docSnap.exists()) {
          setUserData(docSnap.data())
        } else {
          setError(t('profile.userDataNotFound'))
        }
      } catch (err) {
        console.error('Kullanıcı verisi alınırken hata:', err)
        setError(t('profile.userDataFetchError'))
      } finally {
        setLoading(false)
      }
    }

    fetchUserData()
  }, [currentUser, t])

  /**
   * Form güncellendikten sonra, /user/unanswered sayfasına yönlendiren callback
   */
  const handleProfileSave = () => {
    navigate('/')
  }

  const handleCloseSnackbar = () => {
    setError('')
    setSuccess('')
  }

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #fdfbfb 0%, #f3f4f6 40%, #e0f2fe 100%)',
          px: 2
        }}
      >
        <Container maxWidth="sm" disableGutters sx={{ textAlign: 'center' }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 4,
              boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
              backgroundColor: 'rgba(255,255,255,0.96)',
              border: '1px solid rgba(148,163,184,0.25)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <CircularProgress />
            <Typography variant="h6" sx={{ mt: 2 }}>
              {t('profile.loading')}
            </Typography>
          </Paper>
        </Container>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #fdfbfb 0%, #f3f4f6 40%, #e0f2fe 100%)',
        px: 2,
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Grid container spacing={4}>
          {/* Avatar Bölümü */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 4,
                boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
                backgroundColor: 'rgba(255,255,255,0.96)',
                border: '1px solid rgba(148,163,184,0.25)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 'bold', mb: 2 }}
              >
                {t('profile.avatarSection')}
              </Typography>
              <AvatarSection
                userData={userData}
                setUserData={setUserData}
                setError={setError}
                setSuccess={setSuccess}
              />
            </Paper>
          </Grid>

          {/* Profil Bilgileri ve Şifre Güncelleme */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 4,
                boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
                backgroundColor: 'rgba(255,255,255,0.96)',
                border: '1px solid rgba(148,163,184,0.25)',
                backdropFilter: 'blur(10px)',
                mb: 3
              }}
            >


              <ProfileForm
                userData={userData}
                setError={setError}
                setSuccess={setSuccess}
                onProfileSave={handleProfileSave}
              />
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 4,
                boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
                backgroundColor: 'rgba(255,255,255,0.96)',
                border: '1px solid rgba(148,163,184,0.25)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <PasswordUpdateForm
                setError={setError}
                setSuccess={setSuccess}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Snackbar Bildirimleri */}
        <Snackbar
          open={!!error || !!success}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {error ? (
            <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          ) : (
            <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
              {success}
            </Alert>
          )}
        </Snackbar>
      </Container>
    </Box>
  )
}
